import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

/**
 * Cross-component invalidation: fire this after approving/declining a
 * join request so any mounted useUnreadJoinRequests (e.g. TopBar badge)
 * refetches immediately without waiting for realtime UPDATE to arrive.
 */
export const JOIN_REQUESTS_INVALIDATED_EVENT = "lr:join-requests-invalidated";
export function emitJoinRequestsInvalidated() {
  try { window.dispatchEvent(new Event(JOIN_REQUESTS_INVALIDATED_EVENT)); } catch { /* noop */ }
}

/**
 * Counts pending join requests for the shipper's company.
 * Subscribes in realtime to INSERT/UPDATE on shipper_network_join_requests.
 *
 * Self-heal: also refetches on channel SUBSCRIBED (initial + reconnect) and
 * on window focus / tab-visible, so a request that arrives during a
 * CHANNEL_ERROR gap surfaces without a manual refresh.
 */
export function useUnreadJoinRequests(companyId: string | null | undefined) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!companyId) {
      setCount(0);
      return;
    }
    let cancelled = false;

    const fetchCount = async () => {
      const { count: c } = await supabase
        .from("shipper_network_join_requests" as any)
        .select("id", { count: "exact", head: true })
        .eq("shipper_company_id", companyId)
        .eq("status", "pending");
      if (!cancelled) setCount(c ?? 0);
    };
    fetchCount();

    const channel = supabase
      .channel(`unread-join-requests-${companyId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "shipper_network_join_requests",
          filter: `shipper_company_id=eq.${companyId}`,
        },
        () => fetchCount()
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "shipper_network_join_requests",
          filter: `shipper_company_id=eq.${companyId}`,
        },
        () => fetchCount()
      )
      .subscribe((status) => {
        // Self-heal on (re)subscribe — closes CHANNEL_ERROR gap.
        if (status === "SUBSCRIBED") fetchCount();
      });

    // Focus / visibility self-heal, mirrors the DM pattern in ChatContext.
    let timer: ReturnType<typeof setTimeout> | null = null;
    const runDebounced = () => {
      if (timer) return;
      timer = setTimeout(() => { timer = null; fetchCount(); }, 500);
    };
    const onFocus = () => runDebounced();
    const onVis = () => { if (document.visibilityState === "visible") runDebounced(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    // Cross-component invalidation (e.g. after shipper approves/declines).
    const onInvalidated = () => fetchCount();
    window.addEventListener(JOIN_REQUESTS_INVALIDATED_EVENT, onInvalidated);

    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener(JOIN_REQUESTS_INVALIDATED_EVENT, onInvalidated);
      if (timer) clearTimeout(timer);
    };
  }, [companyId]);

  return count;
}
